// src/components/ModelSelectorButton.jsx
import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Typography,
} from "@mui/material";
import { SmartToy } from "@mui/icons-material";
import { ChatContext } from "../context/chatcontext";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/apiKey.js";

// Map models to tiers based on subscription plan
const getModelTier = (modelId, planId) => {
  // Free plan: Only basic models (2 ChatGPT models)
  if (planId === "free") {
    if (["gpt-3.5-turbo", "gpt-4o-mini"].includes(modelId)) {
      return "basic";
    }
    return null; // Not available for free
  }
  
  // Basic plan (₹499): 3 models - 1 higher ChatGPT + 1 Grok + 1 Claude
  if (planId === "basic") {
    if (["gpt-4o", "grok-beta", "claude-3-haiku-20240307"].includes(modelId)) {
      return "standard";
    }
    return null;
  }
  
  // Pro plan (₹1499): 7 models - ChatGPT + Grok + Claude
  if (planId === "pro") {
    // ChatGPT models
    if (["gpt-3.5-turbo", "gpt-4o-mini"].includes(modelId)) return "basic";
    if (["gpt-4o", "gpt-4-turbo-preview"].includes(modelId)) return "standard";
    // Claude models
    if (["claude-3-haiku-20240307", "claude-3-5-sonnet-20241022"].includes(modelId)) return "standard";
    // Grok
    if (["grok-beta"].includes(modelId)) return "standard";
    return null;
  }
  
  // Enterprise (₹1999): 9 models - All models
  if (planId === "enterprise") {
    // ChatGPT models
    if (["gpt-3.5-turbo", "gpt-4o-mini"].includes(modelId)) return "basic";
    if (["gpt-4o", "gpt-4-turbo-preview", "gpt-4"].includes(modelId)) return "premium";
    // Claude models
    if (["claude-3-haiku-20240307"].includes(modelId)) return "standard";
    if (["claude-3-5-sonnet-20241022", "claude-3-opus-20240229"].includes(modelId)) return "premium";
    // Grok
    if (["grok-beta"].includes(modelId)) return "premium";
    return null;
  }
  
  return null;
};

// Custom display names mapping - Map modelId directly to classy, memorable names
const modelIdToCustomName = {
  // ChatGPT models
  "gpt-3.5-turbo": "Alex",
  "gpt-4": "Sam",
  "gpt-4-turbo-preview": "Jordan",
  "gpt-4o": "Taylor",
  "gpt-4o-mini": "Casey",
  // Claude models
  "claude-3-haiku-20240307": "Quinn",
  "claude-3-5-sonnet-20241022": "Cameron",
  "claude-3-opus-20240229": "Chris",
  // Grok models
  "grok-beta": "Gabe",
  // Gemini models (if available)
  "gemini-1.5-pro": "Gemma",
  "gemini-1.5-flash": "Gina",
};

// Fallback mapping for display names (if modelId not found, try displayName)
const customModelNames = {
  "GPT-3.5 Turbo": "Alex",
  "GPT-4": "Sam",
  "GPT-4 Turbo": "Jordan",
  "GPT-4o": "Taylor",
  "GPT-4o-mini": "Casey",
  "Claude": "Clara",
  "Claude Sonnet": "Cameron",
  "Claude Opus": "Chris",
  "Claude Haiku": "Quinn",
  "Gemini": "Gemma",
  "Gemini Pro": "Grace",
  "Gemini 1.5 Pro": "Grace",
  "Gemini 1.5 Flash": "Gina",
  "Grok": "Gus",
  "Grok Beta": "Gabe",
  "Grok 2": "Gus",
};

// Default custom names for fallback (filtered by plan in component)
const defaultCustomModels = [
  { name: "Alex", icon: "⚡", desc: "Fast & efficient", provider: "openai", actualModel: "GPT-3.5 Turbo", modelId: "gpt-3.5-turbo", tier: "basic" },
  { name: "Casey", icon: "✨", desc: "Lightweight", provider: "openai", actualModel: "GPT-4o-mini", modelId: "gpt-4o-mini", tier: "basic" },
  { name: "Sam", icon: "🚀", desc: "Advanced power", provider: "openai", actualModel: "GPT-4", modelId: "gpt-4", tier: "premium" },
  { name: "Jordan", icon: "🌟", desc: "Enhanced speed", provider: "openai", actualModel: "GPT-4 Turbo", modelId: "gpt-4-turbo-preview", tier: "premium" },
  { name: "Taylor", icon: "💎", desc: "Premium quality", provider: "openai", actualModel: "GPT-4o", modelId: "gpt-4o", tier: "standard" },
  { name: "Clara", icon: "🧠", desc: "Intelligent core", provider: "anthropic", actualModel: "Claude", modelId: "claude-3-5-sonnet-20241022", tier: "standard" },
  { name: "Quinn", icon: "🧠", desc: "Fast & smart", provider: "anthropic", actualModel: "Claude Haiku", modelId: "claude-3-haiku-20240307", tier: "standard" },
  { name: "Chris", icon: "🎯", desc: "Precision mode", provider: "anthropic", actualModel: "Claude Opus", modelId: "claude-3-opus-20240229", tier: "premium" },
  { name: "Gemma", icon: "🔮", desc: "Advanced capabilities", provider: "gemini", actualModel: "Gemini", modelId: "gemini-1.5-pro", tier: "premium" },
  { name: "Gus", icon: "🤖", desc: "Powerful processing", provider: "grok", actualModel: "Grok", modelId: "grok-beta", tier: "premium" },
];

export default function ModelSelectorButton() {
  const { selectedModel, setSelectedModel } = useContext(ChatContext);
  const { token, user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("Alex");
  
  const planId = user?.subscription_plan || "free";

  // Fetch available models from backend and map to custom names
  useEffect(() => {
    let isMounted = true;
    const fetchModels = async () => {
      // Don't fetch if not authenticated or user data not available
      if (!token || !user) {
        if (isMounted) setLoading(false);
        return;
      }

      // Small delay to ensure token is valid and session is stable
      await new Promise(r => setTimeout(r, 100));
      if (!isMounted) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/models`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (!isMounted) return;

        // Get response text first to check if it's HTML or JSON
        const responseText = await response.text();
        const contentType = response.headers.get("content-type") || "";
        const isHtml = 
          contentType.includes("text/html") ||
          responseText.trim().toLowerCase().startsWith("<!doctype") || 
          responseText.trim().toLowerCase().startsWith("<html");

        if (isHtml) {
          if (response.status === 401 || response.status === 403) {
            // Silence these specific status codes when they return HTML (likely session transition)
            return;
          }
          console.error("Models API: Server returned HTML instead of JSON. Backend may not be running.");
          setAvailableModels([]);
          setLoading(false);
          return;
        }
        
        if (response.ok) {
          let data;
          try {
            data = JSON.parse(responseText);
          } catch (e) {
            console.error("Failed to parse models response as JSON:", responseText.substring(0, 200));
            setAvailableModels([]);
            setLoading(false);
            return;
          }
          if (data.models && data.models.length > 0) {
            // Map models to custom names - use modelId directly for better matching
            const mappedModels = data.models
              .map((model) => {
                const tier = getModelTier(model.modelId, planId);
                
                // Only include models available for this plan
                if (!tier) return null;
                
                // Get custom name from modelId first, then fallback to displayName
                let customName = modelIdToCustomName[model.modelId];
                if (!customName) {
                  // Fallback: try to extract from displayName
                  const modelName = model.displayName.split(":")[1]?.trim() || model.modelId;
                  customName = customModelNames[modelName] || modelName;
                }
                
                const iconMap = {
                  openai: "⚡",
                  anthropic: "🧠",
                  gemini: "🔮",
                  grok: "🤖",
                };
                const descMap = {
                  openai: "Fast & efficient",
                  anthropic: "Intelligent & precise",
                  gemini: "Advanced capabilities",
                  grok: "Powerful processing",
                };
                
                return {
                  name: customName, // Display only the custom name
                  icon: iconMap[model.provider] || "🤖",
                  desc: descMap[model.provider] || "AI model",
                  provider: model.provider,
                  actualModel: model.modelId, // Store modelId for backend
                  modelId: model.modelId, // Keep modelId for reference
                  tier: tier,
                };
              })
              .filter(model => model !== null); // Remove null entries
            
            setAvailableModels(mappedModels.length > 0 ? mappedModels : defaultCustomModels);
            
            // Initialize display name from stored model or first available model
            if (mappedModels.length > 0) {
              const storedModel = localStorage.getItem("exivis_selectedModel");
              if (storedModel) {
                const customName = modelIdToCustomName[storedModel] || mappedModels.find(m => m.modelId === storedModel)?.name || "Alex";
                setDisplayName(customName);
              } else {
                setDisplayName(mappedModels[0].name);
              }
            }
          } else {
            setAvailableModels(defaultCustomModels);
            setDisplayName("Alex");
          }
        } else {
          // Error response - already checked for HTML above
          if (response.status === 401) {
            // Silently handle 401 during transition
          } else if (response.status === 403) {
            console.warn("Forbidden - insufficient permissions");
          } else {
            console.error(`Models API error: ${response.status} ${response.statusText}`);
          }
          setAvailableModels(defaultCustomModels);
        }
      } catch (error) {
        console.error("Error fetching models:", error);
        if (isMounted) setAvailableModels(defaultCustomModels);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchModels();
    return () => { isMounted = false; };
  }, [token, user, planId]);

  // Update display name when selected model changes
  useEffect(() => {
    if (selectedModel) {
      // Get custom name from modelId mapping first, then fallback
      const customName = modelIdToCustomName[selectedModel] || customModelNames[selectedModel] || selectedModel;
      setDisplayName(customName);
    } else {
      // If no model selected, try to get from localStorage or default
      const storedModel = localStorage.getItem("exivis_selectedModel");
      if (storedModel) {
        const customName = modelIdToCustomName[storedModel] || customModelNames[storedModel] || storedModel;
        setDisplayName(customName);
      } else {
        // Default to first available model's custom name
        if (availableModels.length > 0) {
          setDisplayName(availableModels[0].name);
        }
      }
    }
  }, [selectedModel, availableModels]);

  // Only use models from backend - don't show default models if API fails
  // This ensures we only display models that are actually configured and available
  const models = availableModels;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (modelId) => {
    // Store modelId (not display name) for backend - modelId is what backend expects
    setSelectedModel(modelId);
    localStorage.setItem("exivis_selectedModel", modelId);
    // Get custom name for display from modelId mapping
    const customName = modelIdToCustomName[modelId] || customModelNames[modelId] || modelId;
    setDisplayName(customName);
    handleClose();
  };

  // Group models by tier for display (Basic, Standard, Premium)
  const groupedModels = models.reduce((acc, model) => {
    const tier = model.tier || "basic";
    if (!acc[tier]) {
      acc[tier] = [];
    }
    acc[tier].push(model);
    return acc;
  }, {});
  
  // Define tier order and labels
  const tierOrder = ["premium", "standard", "basic"];
  const tierLabels = {
    premium: "PREMIUM",
    standard: "STANDARD",
    basic: "BASIC",
  };

  return (
    <Box>
      <Button
        onClick={handleClick}
        sx={{
          color: "white",
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "rgba(255,255,255,0.1)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          borderRadius: 2,
          px: 2,
          py: 1,
        }}
        startIcon={<SmartToy sx={{ color: "#38bdf8", fontSize: "18px" }} />}
      >
        <span style={{ fontSize: "14px", fontWeight: 500 }}>
          {displayName || (selectedModel ? (modelIdToCustomName[selectedModel] || customModelNames[selectedModel] || selectedModel) : "Alex")}
        </span>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            bgcolor: "#1a1a1a",
            color: "white",
            borderRadius: "8px",
            border: "1px solid #333",
            maxHeight: "400px",
            minWidth: "250px",
          },
        }}
      >
        {loading ? (
          <MenuItem disabled>
            <ListItemText primary="Loading models..." />
          </MenuItem>
        ) : models.length === 0 ? (
          <MenuItem disabled>
            <ListItemText 
              primary="No models available" 
              secondary="Please configure at least one AI provider API key"
            />
          </MenuItem>
        ) : (
          tierOrder
            .filter(tier => groupedModels[tier] && groupedModels[tier].length > 0)
            .map((tier) => (
            <Box key={tier}>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: tier === "premium" ? "#fbbf24" : tier === "standard" ? "#60a5fa" : "#9ca3af",
                    textTransform: "uppercase",
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                  }}
                >
                  {tierLabels[tier]} {tier === "premium" ? "⭐" : tier === "standard" ? "✨" : ""}
                </Typography>
              </Box>
              {groupedModels[tier].map((model) => {
                // Compare with modelId since selectedModel stores modelId
                const isSelected = selectedModel === model.modelId;
                return (
                  <MenuItem
                    key={model.name}
                    onClick={() => handleSelect(model.modelId)}
                    selected={isSelected}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "rgba(37, 99, 235, 0.2)",
                        "&:hover": {
                          bgcolor: "rgba(37, 99, 235, 0.3)",
                        },
                      },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.05)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <span style={{ fontSize: "18px" }}>{model.icon}</span>
                    </ListItemIcon>
                    <ListItemText
                      primary={model.name}
                      secondary={model.desc}
                      primaryTypographyProps={{
                        sx: { fontSize: "14px", color: "white" },
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: "11px", color: "#9ca3af" },
                      }}
                    />
                    {isSelected && (
                      <span style={{ marginLeft: "10px", color: "#38bdf8", fontWeight: "bold" }}>✓</span>
                    )}
                  </MenuItem>
                );
              })}
              {tierOrder.filter(t => groupedModels[t] && groupedModels[t].length > 0).indexOf(tier) < tierOrder.filter(t => groupedModels[t] && groupedModels[t].length > 0).length - 1 && (
                <Divider sx={{ borderColor: "#333", my: 0.5 }} />
              )}
            </Box>
          ))
        )}
      </Menu>
    </Box>
  );
}
